import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { consultarSessao, sair } from './autenticacao-api.js';
import type { Sessao } from './tipos.js';

/**
 * Estado da sessão do painel.
 *
 * `carregando` é o estado inicial obrigatório: o cookie é HttpOnly, então a
 * única forma de saber se há sessão é perguntar ao middleware. Enquanto a
 * resposta não chega, o painel não pode nem mostrar conteúdo nem mandar para
 * o login — ambos seriam chute.
 */
export type EstadoSessao =
  | { readonly situacao: 'carregando' }
  | { readonly situacao: 'anonimo' }
  | { readonly situacao: 'autenticado'; readonly sessao: Sessao };

type ValorContexto = {
  readonly estado: EstadoSessao;
  /** Registra a sessão recém-criada pelo formulário de login. */
  readonly registrarEntrada: (sessao: Sessao) => void;
  readonly encerrarSessao: () => Promise<void>;
};

const SessaoContexto = createContext<ValorContexto | null>(null);

export function ProvedorSessao({ children }: { readonly children: ReactNode }) {
  const [estado, definirEstado] = useState<EstadoSessao>({ situacao: 'carregando' });

  useEffect(() => {
    let ativo = true;

    consultarSessao()
      .then((sessao) => {
        if (!ativo) {
          return;
        }
        definirEstado(sessao ? { situacao: 'autenticado', sessao } : { situacao: 'anonimo' });
      })
      .catch(() => {
        // Middleware fora do ar no boot: sem prova de sessão, o painel trata
        // como anônimo. O erro real aparece na tentativa de login.
        if (ativo) {
          definirEstado({ situacao: 'anonimo' });
        }
      });

    return () => {
      ativo = false;
    };
  }, []);

  const registrarEntrada = useCallback((sessao: Sessao) => {
    definirEstado({ situacao: 'autenticado', sessao });
  }, []);

  const encerrarSessao = useCallback(async () => {
    try {
      await sair();
    } catch {
      // Middleware inalcançável: a tela cai para o login mesmo assim — manter
      // "autenticado" depois de um Sair seria mentir para quem clicou. O
      // cookie só o servidor apaga, então uma recarga pode reencontrar a
      // sessão; é o máximo que o navegador consegue garantir sozinho.
    }

    definirEstado({ situacao: 'anonimo' });
  }, []);

  const valor = useMemo<ValorContexto>(
    () => ({ estado, registrarEntrada, encerrarSessao }),
    [estado, registrarEntrada, encerrarSessao],
  );

  return <SessaoContexto.Provider value={valor}>{children}</SessaoContexto.Provider>;
}

export function useSessao(): ValorContexto {
  const valor = useContext(SessaoContexto);

  if (!valor) {
    throw new Error('useSessao exige que a árvore esteja dentro de <ProvedorSessao>.');
  }

  return valor;
}

/**
 * Sessão garantida — só pode ser chamada dentro de uma rota protegida, onde a
 * ausência de sessão já redirecionou para o login.
 */
export function useSessaoAutenticada(): Sessao {
  const { estado } = useSessao();

  if (estado.situacao !== 'autenticado') {
    throw new Error('useSessaoAutenticada exige uma rota protegida.');
  }

  return estado.sessao;
}
